import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Loader } from 'fronton-react';
import { useSelector } from 'react-redux';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { loader, displayNone } from 'common/styles';
import { getTitle } from 'features/page-model/ducks/get-model';
import { useOptimisedImages } from '@navi-toolbox/hooks';
import { analyticsUtils } from 'utils';
import { Container, Image, ImagePreview, ModalContent } from './style';
import { useFrontImageAlt } from '../hooks/use-front-image-alt';

export const DATA_QA_STORE_FRONT_IMAGE = 'store-front-image';
export const DATA_QA_STORE_FRONT_IMAGE_SOURCE = 'store-front-image-source';
export const DATA_QA_STORE_FRONT_IMAGE_MODAL = 'store-front-image-modal';
export const DATA_QA_STORE_FRONT_IMAGE_MODAL_SOURCE = 'store-front-image-modal-source';

type Props = {
  imageUrl: string;
};

export const StoreFrontImage: FC<Props> = ({ imageUrl }) => {
  const { t } = useTranslation();
  const title = useSelector(getTitle);
  const { mobile } = useBreakpoints() ?? {};
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const imageAlt = useFrontImageAlt();
  const shopPhoto = t('shopPhoto');

  const optimizedThumbImage = useOptimisedImages({
    src: imageUrl,
    sizes: {
      rest: {
        h: '150',
      },
    },
  });
  const optimizedImage = useOptimisedImages({
    src: imageUrl,
    sizes: {
      rest: {
        h: '800',
      },
    },
  });

  const thumbnail = optimizedThumbImage.rest?.x ?? undefined;
  const image = optimizedImage.rest?.x ?? undefined;

  const handleOpen = useCallback(() => {
    setOpen(true);
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: title, // название блока
      elementText: shopPhoto, // текст элемента
    });
  }, [title, shopPhoto]);

  const handleClose = useCallback(() => {
    setOpen(false);
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'shopPage', // блок, в котором расположен элемент
      elementName: `${title} / ${shopPhoto}`, // название блока
      elementText: t('ui.close'), // текст элемента
    });
  }, [title, shopPhoto, t]);

  const onLoad = () => {
    setIsLoading(false);
  };

  return (
    <Container>
      <picture>
        <source
          srcSet={`${optimizedThumbImage.rest?.xx} 2x, ${optimizedThumbImage.rest?.x} 1x`}
          data-qa={DATA_QA_STORE_FRONT_IMAGE_SOURCE}
        />
        <ImagePreview
          onClick={handleOpen}
          width={156}
          height={104}
          src={thumbnail}
          alt={imageAlt}
          data-qa={DATA_QA_STORE_FRONT_IMAGE}
        />
      </picture>
      <Modal onClose={handleClose} size="l" variant={mobile ? 'full-screen' : 'simple'} show={open}>
        {isLoading && <Loader size="m" className={loader} />}
        <ModalContent className={isLoading ? displayNone : ''}>
          <picture>
            <source
              srcSet={`${optimizedImage.rest?.xx} 2x, ${optimizedImage.rest?.x} 1x`}
              data-qa={DATA_QA_STORE_FRONT_IMAGE_MODAL_SOURCE}
            />
            <Image src={image} alt={imageAlt} data-qa={DATA_QA_STORE_FRONT_IMAGE_MODAL} onLoad={onLoad} />
          </picture>
        </ModalContent>
      </Modal>
    </Container>
  );
};
